import { useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { Settings, LogOut, Home, Squirrel, BarChart3, Computer, GamepadDirectional, GraduationCap, HardDrive, BriefcaseBusiness, Menu, X } from "lucide-react";
import * as LucideIcons from "lucide-react";

export const API_URL = process.env.NODE_ENV === "production"
  ? "https://www.junior-i2c.com/php"
  : "http://localhost/php";

export const Card = ({ children, className = "" }) => (
  <div className={`bg-white/50 rounded-2xl shadow p-1 ${className}`}>{children}</div>
);

export const CardContent = ({ children, className = "" }) => (
  <div className={className}>{children}</div>
);

export const Button = ({ children, className = "", variant = "default", ...props }) => {
  const base =
    "px-4 py-2 rounded-xl font-medium transition-colors duration-200 focus:outline-none";
  const variants = {
    default: "bg-blue-600 text-white hover:bg-blue-700",
    outline: "border border-gray-300 text-gray-700 hover:bg-gray-100",
  };

  return (
    <button className={`${base} ${variants[variant]} ${className}`} {...props}>
      {children}
    </button>
  );
};

const Dashboard = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [obtenirScoresKahoot, setObtenirScoresKahoot] = useState([]);
  const [caActuel, setCaActuel] = useState(0);
  const [compteurEtudes, setCompteurEtudes] = useState(0);
  const [etudes, setEtudes] = useState([]);
  const [membres, setMembres] = useState([]);
  const [participantsSelectionnes, setParticipantsSelectionnes] = useState([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest('.dropdown-container')) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  useEffect(() => {
    const storedUser = JSON.parse(localStorage.getItem('user'));
    if (!storedUser) {
      navigate('/');
    } else {
      setUser(storedUser);
      setParticipantsSelectionnes([storedUser]);

      fetch(`${API_URL}/obtenirMembresReunion.php?identifiant=${storedUser.identifiant}`)
        .then(res => res.json())
        .then(data => setMembres(data))
        .catch(err => console.error(err));
    }

    fetch(`${API_URL}/obtenirScoresKahoot.php`)
      .then(res => res.json())
      .then(data => setObtenirScoresKahoot(data))
      .catch(err => console.error(err));

    fetch(`${API_URL}/chiffreAffairesActuel.php`)
      .then(res => res.json())
      .then(data => {
        if (data.length > 0) {
          const ca = data[0].chiffreAffairesActuel;
          setCaActuel(ca !== null ? ca : 0);
        } else setCaActuel(0);
      })
      .catch(err => {
        console.error(err);
        setCaActuel(0);
      });

    fetch(`${API_URL}/nombreEtudesEnCours.php`)
      .then(res => res.json())
      .then(data => {
        if (data.length > 0) setCompteurEtudes(data[0]['COUNT(nom)']); 
      })
      .catch(err => console.error(err));

    fetch(`${API_URL}/obtenirEtudesEnCours.php`)
      .then(res => res.json())
      .then(data => setEtudes(data))
      .catch(err => console.error(err));

  }, [navigate]);

  const seDeconnecter = () => {
    localStorage.removeItem('user');
    navigate('/');
  };

  if (!user) return null;

  const memberOfMonth = obtenirScoresKahoot[0];

  const trouverCreneau = () => {
    const emails = participantsSelectionnes.map(p => `${p.identifiant}@ensc.fr`);
    const start = new Date();
    const end = new Date(start.getTime() + 60*60*1000);
    const formatDate = (date) => date.toISOString().replace(/-|:|\.\d+/g,"");
    const url = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent("Réunion i2c")}&details=${encodeURIComponent("Réunion créée depuis le dashboard")}&dates=${formatDate(start)}/${formatDate(end)}&add=${emails.join(",")}&src=c_14b5c44b9c5537929ba2e8f29a671fd840f32fbc7c309078c19bb6ba19a914e1@group.calendar.google.com`;
    window.open(url, "_blank");
  };

  return (
    <div className="flex min-h-screen bg-gray-50 relative overflow-x-hidden">
      
      {isMobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-20 lg:hidden"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* ---- BARRE DE NAVIGATION ---- */}
      <div className={`
        fixed inset-y-0 left-0 z-30 w-64 bg-white shadow-md transform transition-transform duration-300 ease-in-out
        lg:relative lg:translate-x-0
        ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <BarreNavigation 
          user={user} 
          deconnexion={seDeconnecter} 
          onNavigate={() => setIsMobileMenuOpen(false)} 
        />
        {/* Croix pour fermer sur mobile */}
        <button 
            onClick={() => setIsMobileMenuOpen(false)}
            className="absolute top-4 right-4 lg:hidden text-gray-500"
        >
            <X size={24} />
        </button>
      </div>

      {/* ---- MAIN CONTENT ---- */}
      <main className="flex-1 p-4 lg:p-8 w-full">
        <header className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <button 
                className="lg:hidden p-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                onClick={() => setIsMobileMenuOpen(true)}
            >
              <Menu size={24} />
            </button>
            <h1 className="text-2xl text-gris-sombre font-bold">Dashboard</h1>
          </div>
          
          <div className="flex items-center space-x-4">
            <Button variant="outline" onClick={seDeconnecter}>
              <span className="hidden sm:inline">Se déconnecter</span>
              <LogOut className="sm:hidden w-4 h-4" />
            </Button>
          </div>
        </header>

        {/* ---- CHIFFRES ---- */}
        <section
        className="relative mb-6 rounded-2xl overflow-hidden"
        style={{ backgroundImage: "url('/images/bg_skyline.avif')", backgroundSize: 'cover', backgroundPosition: 'center 80%' }}
        >
        <div className="absolute inset-0 bg-black bg-opacity-30"></div>

        <div className="relative p-6">
          <h2 className="text-2xl font-bold mb-6">i2c en quelques chiffres</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Chiffre titre="CA actuel" valeur={`${caActuel} €`}/>
            <Chiffre titre="CA visé" valeur="13000 €"/>
            <Chiffre titre={`${compteurEtudes <= 1 ? "Étude" : "Études"} en cours`} valeur={compteurEtudes}/>
          </div>
        </div>
        </section>

        <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* ---- MEMBRE DU MOIS ---- */}
          {memberOfMonth && (
            <Card>
              <CardContent className="p-4">
                <h3 className="font-semibold mb-3">LE MEMBRE DU <span className="text-bleu-je">MOIS</span> :</h3>
                <div className="flex flex-col items-center">
                  <img
                    src={`/images/PhotosMandat/${memberOfMonth.prenom} ${memberOfMonth.nom}.avif`}
                    alt="Membre du mois"
                    className="w-20 h-20 rounded-full mb-2 object-cover"
                  />
                  <p className="font-semibold text-blue-600">{memberOfMonth.prenom} {memberOfMonth.nom}</p>
                  <p className="text-sm text-gray-500 text-center">On peut te féliciter pour ton implication !</p>
                </div>
              </CardContent>
            </Card>
          )}

          {/* ---- CLASSEMENT KAHOOT ---- */}
          <Card>
            <CardContent className="p-4">
              <h3 className="font-semibold mb-5">
                Classement <span className="text-purple-700">Kahoot</span> :
              </h3>
              <ul className="space-y-2">
                {obtenirScoresKahoot.slice(0, 3).map((membre, index) => {
                  let medaille = '';
                  if (index === 0) medaille = '🏆';
                  else if (index === 1) medaille = '🥈';
                  else if (index === 2) medaille = '🥉';

                  return (
                    <RangKahoot key={index} image={`/images/PhotosMandat/${membre.prenom} ${membre.nom}.avif`}
                      name={`${membre.prenom} ${membre.nom}`} points={`${membre.score_total} pts ${medaille}`}/>
                  );
                })}
              </ul>
            </CardContent>
          </Card>

          {/* ---- ORGANISER UNE RÉUNION ---- */}
          <Card className="md:col-span-2 lg:col-span-1">
            <CardContent className="p-4">
              <h3 className="font-semibold mb-2 text-blue-600">Organiser une réunion</h3>

              <div className="flex items-center flex-wrap gap-2 mb-3">
                <span className="text-sm text-gray-500">Participants :</span>
                {participantsSelectionnes.map((p, idx) => {
                  const estUtilisateur = p.id === user.id;

                  return (
                    <div key={p.id || idx} className={`relative w-8 h-8 rounded-full border-2 border-bleu-je overflow-hidden cursor-pointer 
                      ${estUtilisateur ? 'border-bleu-je' : 'border-bleu-je hover:border-red-500 hover:brightness-75'}`}
                      onClick={() => {
                        if (!estUtilisateur) setParticipantsSelectionnes(prev => prev.filter(part => part.id !== p.id));
                      }}
                    >
                      <img src={`/images/PhotosMandat/${p.prenom} ${p.nom}.avif`} alt={p.prenom}
                        className={`w-full h-full object-cover rounded-full
                        ${!estUtilisateur ? 'hover:opacity-70' : ''}`}
                      />
                      {!estUtilisateur && (
                        <div className="absolute inset-0 rounded-full hover:bg-red-500 hover:bg-opacity-25"></div>
                      )}
                    </div>
                  );
                })}
              </div>

              <div className="dropdown-container relative w-full">
                <Button variant="outline" className="w-full" onClick={() => setIsDropdownOpen(!isDropdownOpen)}>
                  + Ajouter un participant
                </Button>
                {isDropdownOpen && (
                  <ul className="absolute z-10 w-full bg-white border border-gray-300 max-h-60 overflow-y-auto mt-1 rounded shadow">
                    {membres
                      .filter(m => !participantsSelectionnes.find(p => p.id === m.id))
                      .map(m => (
                        <li key={m.id} className="px-2 py-1 hover:bg-gray-100 cursor-pointer"
                          onClick={() => {
                            setParticipantsSelectionnes(prev => [...prev, m]);
                            setIsDropdownOpen(false);
                          }}
                        >
                          {m.prenom} {m.nom}
                        </li>
                      ))}
                  </ul>
                )}
              </div>

              <Button className="w-full mt-2" onClick={trouverCreneau}>Trouver un créneau</Button>
            </CardContent>
          </Card>
        </section>

        {/* ---- CALENDRIER + ÉTUDES ---- */}
        <section className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
          <Card className="lg:col-span-2">
            <CardContent className="p-4">
              <h3 className="font-semibold mb-2">Calendrier</h3>
              <div className="overflow-hidden rounded-lg">
                  <iframe
                    src="https://calendar.google.com/calendar/embed?src=c_14b5c44b9c5537929ba2e8f29a671fd840f32fbc7c309078c19bb6ba19a914e1%40group.calendar.google.com&ctz=Europe%2FParis"
                    style={{ border: 0 }} width="100%" height="400" title="Agenda Google"
                  ></iframe>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <h3 className="font-semibold mb-3 text-blue-600">Les études en cours</h3>
              <ul className="space-y-4">
                {etudes.map((etude, index) => (
                  <Etude key={index} title={`Étude ${index + 1}`} nom={etude.nom} chiffrage={`${etude.chiffrage} €`} couleur={etude.couleur} icone={etude.icone}/>
                ))}
              </ul>
              <Button variant="outline" className="w-full mt-4" onClick={() => navigate("/etudes")}>Voir toutes les études</Button>
            </CardContent>
          </Card>
        </section>
      </main>
    </div>
  );
};

// ---- COMPOSANTS SECONDAIRES ----
export const BarreNavigation = ({ user, deconnexion, onNavigate }) => {
  const navigate = useNavigate();

  const BarreNavigationItem = ({ icon, text, link }) => {
    const ouvrirLien = () => {
      if (!link) return;
      if (link.startsWith("/")) {
          navigate(link);
          if (onNavigate) onNavigate(); // Fermer le menu si on est sur mobile
      }
      else window.open(link, "_blank");
    };

    return (
      <div className="flex items-center space-x-3 p-2 rounded-md hover:bg-blue-100 cursor-pointer" onClick={ouvrirLien}>
        {icon}<span>{text}</span>
      </div>
    );
  };

  return (
    <aside className="h-full flex flex-col justify-between">
      <div>
        <div className="flex items-center justify-center py-6 border-b">
          <img src="/images/logo_i2c_noir.svg" alt="Logo d'i2c" className="h-16" />
        </div>

        <nav className="mt-6 space-y-2 px-4 overflow-y-auto">
          <BarreNavigationItem icon={<Home size={18}/>} text="Menu" link="/dashboard"/>
          <BarreNavigationItem icon={<Squirrel size={18}/>} text="To-do list" link="/todolist"/>
          <BarreNavigationItem icon={<BarChart3 size={18}/>} text="Études" link="/etudes"/>
          <BarreNavigationItem icon={<Computer size={18}/>} text="SI" link="/si"/>
          <BarreNavigationItem icon={<GamepadDirectional size={18}/>} text="Kahoot" link="/kahoot"/>
          <BarreNavigationItem icon={<GraduationCap size={18}/>} text="Alumni" link="https://docs.google.com/spreadsheets/d/10zEsqCCFSbbGmdL92EtqXFt8eqZ6nLQ5"/>
          <BarreNavigationItem icon={<HardDrive size={18}/>} text="Drive" link="https://drive.google.com/drive/u/1/folders/1-6zf8OaJsb8s_CpKjZiTzIH1rWA6bkT-"/>
          <BarreNavigationItem icon={<BriefcaseBusiness size={18}/>} text="Inter-Mandat" link="https://drive.google.com/drive/u/1/folders/1ge-CFA7s9V_FhfI3Zln8G_M6VrFp1aEC"/>
        </nav>
      </div>

      <div className="p-4 border-t">
        <div className="flex items-center mb-3">
          <img src={`/images/PhotosMandat/${user.prenom} ${user.nom}.avif`} alt="Membre" className="w-10 h-10 rounded-full mr-3 object-cover"/>
          <div className="overflow-hidden">
            <p className="font-semibold truncate">{user.prenom} {user.nom}</p>
            <p className="text-sm text-gray-500 truncate">{user.poste}</p>
          </div>
        </div>

        <div className="flex items-center justify-between text-gray-600 text-sm">
          <Settings size={16} className="cursor-pointer" onClick={() => navigate("/parametres")}/>
          <LogOut size={16} className="ml-auto cursor-pointer" onClick={deconnexion}/>
        </div>
      </div>
    </aside>
  );
};

const Chiffre = ({ titre, valeur }) => (
  <Card>
    <CardContent className="text-center">
      <p className="text-2xl font-bold">{valeur}</p>
      <p className="text-sm">{titre}</p>
    </CardContent>
  </Card>
);

const RangKahoot = ({ image, name, points }) => (
  <li className="flex items-center justify-between">
    <div className="flex items-center space-x-3">
      <img src={image} alt={name} className="w-8 h-8 rounded-full object-cover"/>
      <span>{name}</span>
    </div>
    <span className="text-sm text-gray-600">{points}</span>
  </li>
);

const Etude = ({ title, nom, chiffrage, icone, couleur }) => {
  const IconComponent = LucideIcons[icone] || LucideIcons.FileText;

  return (
    <li className="flex justify-between items-center">
      <div className="flex items-center space-x-3">
        <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-full" style={{ backgroundColor: couleur }}>
          <IconComponent size={16} color="white" />
        </div>
        <div className="overflow-hidden">
          <p className="font-semibold truncate">{title}</p>
          <p className="text-sm text-gray-500 truncate">{nom}</p>
        </div>
      </div>
      <span className="text-sm font-semibold whitespace-nowrap ml-2">{chiffrage}</span>
    </li>
  );
};

export default Dashboard;